// src/pages/items/ProductsMenu.jsx
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDoc, getDocs, doc, query } from "firebase/firestore";
import { useSearchParams } from "react-router-dom";
import { db } from "../../../firebaseConfig";
import {
  ChevronLeftIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
} from "@heroicons/react/24/outline";

import logo from "../../images/coffee-station-logo.png";

/* =========================
   B/W THEME
   ========================= */
const BW = {
  black: "#0B0B0D",
  white: "#FFFFFF",
  g50: "#FAFAFB",
  g100: "#F2F2F5",
  g200: "#E6E6EB",
  g300: "#D2D2DA",
  g400: "#A8A8B4",
  g500: "#7A7A86",
  g600: "#5B5B66",
  g700: "#3F3F48",
};

function formatIQD(n) {
  const x = Number(n ?? 0);
  return Number.isFinite(x) ? x.toLocaleString("en-US") : "0";
}

const DEFAULT_OWNER_ID = "Yx6fz8iaACXkggifX7TYiWtVGsQ2";
const CAT_NONE = "__none__";

export default function ProductsMenu() {
  const [searchParams] = useSearchParams();
  const ownerId = searchParams.get("uid") || DEFAULT_OWNER_ID;

  const [storeName, setStoreName] = useState("");
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // ✅ فئات أولاً (null = شاشة الفئات)
  const [selectedCat, setSelectedCat] = useState(null);

  // داخل شاشة المنتجات
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name"); // name | priceAsc | priceDesc

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr("");

        // categories
        const catsSnap = await getDocs(collection(db, "categories"));
        const cats = catsSnap.docs.map((d) => ({
          id: d.id,
          ...(d.data() || {}),
        }));
        setCategories(cats);

        if (!ownerId) {
          setItems([]);
          setStoreName("");
          setErr("أضف ?uid= إلى رابط الصفحة لعرض المنيو علنًا.");
          return;
        }

        // store name
        try {
          const uSnap = await getDoc(doc(db, "users", ownerId));
          const uData = uSnap.exists() ? uSnap.data() : null;
          setStoreName(
            uData?.storeName || uData?.displayName || uData?.name || "Coffee Station"
          );
        } catch {
          setStoreName("Coffee Station");
        }

        // products
        const productsSnap = await getDocs(
          query(collection(db, "users", ownerId, "products"))
        );
        const list = productsSnap.docs.map((d) => ({
          id: d.id,
          ...(d.data() || {}),
        }));
        setItems(list);
      } catch (e) {
        console.error(e);
        setErr("تعذّر جلب البيانات.");
      } finally {
        setLoading(false);
      }
    })();
  }, [ownerId]);

  const title = storeName?.trim() ? storeName : "Coffee Station";

  const catNameById = useMemo(() => {
    const map = {};
    for (const c of categories) map[c.id] = c.name || c.id;
    return map;
  }, [categories]);

  const countsByCat = useMemo(() => {
    const counts = {};
    for (const p of items) {
      const cid = p.categoryId || CAT_NONE;
      counts[cid] = (counts[cid] || 0) + 1;
    }
    return counts;
  }, [items]);

  // ✅ فقط الفئات التي لديها منتجات
  const categoriesWithCounts = useMemo(() => {
    const base = categories
      .map((c) => ({
        id: c.id,
        name: c.name || c.id,
        count: countsByCat[c.id] || 0,
      }))
      .filter((c) => c.count > 0)
      .sort((a, b) => String(a.name).localeCompare(String(b.name), "ar"));

    if (countsByCat[CAT_NONE]) {
      base.push({
        id: CAT_NONE,
        name: "بدون قسم",
        count: countsByCat[CAT_NONE],
      });
    }
    return base;
  }, [categories, countsByCat]);

  const selectedCatLabel = useMemo(() => {
    if (selectedCat === null) return "";
    if (selectedCat === CAT_NONE) return "بدون قسم";
    return catNameById[selectedCat] || "فئة";
  }, [selectedCat, catNameById]);

  const filteredProducts = useMemo(() => {
    if (selectedCat === null) return [];

    let list =
      selectedCat === CAT_NONE
        ? items.filter((p) => !p.categoryId)
        : items.filter((p) => p.categoryId === selectedCat);

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const name = String(p.name || "").toLowerCase();
        const desc = String(p.description || "").toLowerCase();
        const barcode = String(p.barcode || "").toLowerCase();
        return name.includes(q) || desc.includes(q) || barcode.includes(q);
      });
    }

    const clone = [...list];
    clone.sort((a, b) => {
      if (sortBy === "priceAsc") return Number(a.price || 0) - Number(b.price || 0);
      if (sortBy === "priceDesc") return Number(b.price || 0) - Number(a.price || 0);
      return String(a.name || "").localeCompare(String(b.name || ""), "ar");
    });

    return clone;
  }, [items, selectedCat, search, sortBy]);

  function backToCategories() {
    setSelectedCat(null);
    setSearch("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <div
      dir="rtl"
      className="min-h-screen px-4 py-8"
      style={{
        background: `
          radial-gradient(900px 420px at 100% -10%, rgba(0,0,0,.06), transparent 55%),
          linear-gradient(180deg, ${BW.white} 0%, ${BW.g50} 55%, ${BW.white} 100%)
        `,
      }}
    >
      <div className="mx-auto max-w-5xl space-y-5">
        {/* =========================
            Header (No Back Button)
           ========================= */}
        <header
          className="rounded-[28px] border bg-white shadow-sm overflow-hidden"
          style={{ borderColor: BW.g200 }}
        >
          <div
            className="h-2"
            style={{
              background: `linear-gradient(90deg, ${BW.black}, ${BW.g700}, ${BW.black})`,
            }}
          />
          <div className="p-5 sm:p-7">
            <div className="flex items-center gap-3">
              <div
                className="h-14 w-14 rounded-2xl border grid place-items-center bg-white"
                style={{ borderColor: BW.g200 }}
              >
                <img
                  src={logo}
                  alt="Coffee Station"
                  className="h-10 w-10 object-contain"
                  loading="lazy"
                />
              </div>

              <div className="text-right">
                <div
                  className="text-[11px] tracking-[0.22em] font-semibold"
                  style={{ color: BW.g600 }}
                >
                  ELECTRONIC MENU
                </div>
                <h1
                  className="mt-1 text-2xl sm:text-3xl font-extrabold font-tajawal"
                  style={{ color: BW.black }}
                >
                  {title}
                </h1>
              </div>
            </div>
          </div>
        </header>

        {/* =========================
            Content
           ========================= */}
        {loading ? (
          <LoadingSkeletonClean />
        ) : err ? (
          <Panel>
            <p className="font-tajawal text-sm" style={{ color: "#B91C1C" }}>
              {err}
            </p>
          </Panel>
        ) : items.length === 0 ? (
          <Panel>
            <p className="font-tajawal">لا توجد منتجات بعد.</p>
          </Panel>
        ) : (
          <>
            {/* =========================
                Categories View
               ========================= */}
            {selectedCat === null ? (
              <section className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {categoriesWithCounts.map((c, i) => (
                    <CategoryTile
                      key={c.id}
                      title={c.name}
                      count={c.count}
                      onClick={() => setSelectedCat(c.id)}
                      dark={i % 3 === 0}
                    />
                  ))}
                </div>
              </section>
            ) : (
              /* =========================
                 Products View
                 ========================= */
              <section className="space-y-4">
                {/* Top line: category title + back to categories only */}
                <div className="flex items-center justify-between gap-2">
                  <div className="text-right">
                    <h2
                      className="text-xl sm:text-2xl font-extrabold font-tajawal"
                      style={{ color: BW.black }}
                    >
                      {selectedCatLabel}
                    </h2>
                  </div>

                  <button
                    onClick={backToCategories}
                    className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold border transition hover:shadow-sm"
                    style={{
                      background: BW.white,
                      color: BW.black,
                      borderColor: BW.g200,
                    }}
                    title="رجوع للفئات"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                    الفئات
                  </button>
                </div>

                {/* Tools */}
                <div
                  className="rounded-[22px] border bg-white shadow-sm p-3 sm:p-4"
                  style={{ borderColor: BW.g200 }}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                    {/* Search */}
                    <div className="relative w-full md:w-[420px]">
                      <MagnifyingGlassIcon
                        className="h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2"
                        style={{ color: BW.g500 }}
                      />
                      <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="بحث…"
                        className="w-full rounded-2xl border bg-white pr-10 pl-3 py-2 text-sm outline-none font-tajawal"
                        style={{ borderColor: BW.g200, color: BW.black }}
                      />
                    </div>

                    {/* Sort */}
                    <div className="relative w-full md:w-auto">
                      <AdjustmentsHorizontalIcon
                        className="h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2"
                        style={{ color: BW.g500 }}
                      />
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="w-full md:w-auto rounded-2xl border bg-white pr-10 pl-8 py-2 text-sm outline-none font-tajawal"
                        style={{ borderColor: BW.g200, color: BW.black }}
                        title="ترتيب"
                      >
                        <option value="name">الاسم</option>
                        <option value="priceAsc">السعر ↑</option>
                        <option value="priceDesc">السعر ↓</option>
                      </select>
                    </div>
                  </div>
                </div>

                {filteredProducts.length === 0 ? (
                  <Panel>
                    <p className="font-tajawal">لا توجد منتجات.</p>
                  </Panel>
                ) : (
                  <div className="space-y-3">
                    {filteredProducts.map((p) => (
                      <ProductRowClean key={p.id} p={p} />
                    ))}
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* =========================
   UI Components
   ========================= */

function Panel({ children }) {
  return (
    <div
      className="rounded-[22px] border bg-white p-5 shadow-sm"
      style={{ borderColor: BW.g200 }}
    >
      {children}
    </div>
  );
}

function CategoryTile({ title, count, onClick, dark = false }) {
  return (
    <button
      onClick={onClick}
      className="group relative overflow-hidden rounded-[26px] border p-4 text-right shadow-sm transition hover:-translate-y-[1px] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-black/10"
      style={{
        borderColor: BW.g200,
        background: dark ? BW.black : BW.white,
        color: dark ? BW.white : BW.black,
      }}
      title={title}
    >
      {/* pattern */}
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{
          background: dark
            ? "repeating-linear-gradient(90deg, rgba(255,255,255,.35) 0px, rgba(255,255,255,.35) 1px, transparent 1px, transparent 14px)"
            : "repeating-linear-gradient(90deg, rgba(0,0,0,.25) 0px, rgba(0,0,0,.25) 1px, transparent 1px, transparent 14px)",
        }}
      />

      <div className="relative">
        <div className="flex items-start justify-between gap-2">
          <div className="text-[15px] sm:text-base font-extrabold font-tajawal leading-snug line-clamp-2">
            {title}
          </div>

          <span
            className="inline-flex min-w-9 h-9 items-center justify-center rounded-full text-xs font-extrabold border"
            style={{
              borderColor: dark ? "rgba(255,255,255,.18)" : BW.g200,
              background: dark ? "rgba(255,255,255,.08)" : BW.g50,
              color: dark ? BW.white : BW.g700,
            }}
          >
            {count}
          </span>
        </div>

        <div
          className="mt-4 h-[2px] w-10 rounded-full transition group-hover:w-16"
          style={{ background: dark ? BW.white : BW.black }}
        />
      </div>
    </button>
  );
}

function ProductRowClean({ p }) {
  const hasImg = Boolean(p.imageURL);
  const price = formatIQD(p.price);

  return (
    <article
      className="rounded-[26px] border bg-white shadow-sm overflow-hidden transition hover:shadow-md"
      style={{ borderColor: BW.g200 }}
    >
      <div className="grid grid-cols-1 sm:grid-cols-[190px_1fr]">
        {/* Image */}
        <div className="relative">
          <div className="h-52 sm:h-full w-full bg-gray-100">
            {hasImg ? (
              <img
                src={p.imageURL}
                alt={p.name || "صورة المنتج"}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="h-full w-full grid place-items-center">
                <div
                  className="rounded-2xl px-3 py-1.5 text-sm border font-tajawal"
                  style={{
                    borderColor: BW.g200,
                    background: BW.g50,
                    color: BW.g600,
                  }}
                >
                  بدون صورة
                </div>
              </div>
            )}
          </div>

          {/* price */}
          <div className="absolute top-3 left-3">
            <div
              className="rounded-xl px-3 py-1.5 text-sm font-extrabold border backdrop-blur-sm"
              style={{
                background: "rgba(255,255,255,.92)",
                borderColor: "rgba(0,0,0,.08)",
                color: BW.black,
              }}
            >
              {price} <span className="font-normal">د.ع</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-5">
          <h3
            className="text-lg sm:text-xl font-extrabold font-tajawal"
            style={{ color: BW.black }}
          >
            {p.name || "بدون اسم"}
          </h3>

          {p.description ? (
            <p
              className="mt-2 text-[14px] leading-7 font-tajawal whitespace-pre-wrap"
              style={{ color: BW.g700 }}
            >
              {p.description}
            </p>
          ) : null}

          {p.barcode ? (
            <div className="mt-3">
              <span className="font-mono text-xs" style={{ color: BW.g400 }}>
                #{p.barcode}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function LoadingSkeletonClean() {
  return (
    <div className="space-y-4">
      <div
        className="rounded-[28px] border bg-white p-6 shadow-sm animate-pulse"
        style={{ borderColor: BW.g200 }}
      >
        <div className="flex items-center gap-3">
          <div className="h-14 w-14 rounded-2xl bg-gray-100" />
          <div className="flex-1">
            <div className="h-3 bg-gray-100 rounded w-40" />
            <div className="mt-2 h-6 bg-gray-100 rounded w-56" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="rounded-[26px] border bg-white p-4 shadow-sm animate-pulse"
            style={{ borderColor: BW.g200 }}
          >
            <div className="h-5 bg-gray-100 rounded w-3/4" />
            <div className="mt-4 h-9 w-9 bg-gray-100 rounded-full" />
            <div className="mt-4 h-2 bg-gray-100 rounded w-10" />
          </div>
        ))}
      </div>
    </div>
  );
}
